import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CompanySearch } from '@/components/CompanySearch';
import type { Card, CardType, MetricDef, MetricModule } from '@/api/boards';
import { CARD_LABEL, CARD_SIZE, CARD_TYPES_FOR, MODULE_LABEL, newCardId } from '@/lib/boards';
import { cn } from '@/lib/utils';

/**
 * Add / edit one dashboard card. Metric cards: pick a metric from the catalogue (grouped by
 * module), then the card type it can show, title, client and thresholds. Custom cards: a Markdown
 * note, a set of links (Cosmos pages or external URLs) or a clock.
 */

type Draft = Card;

const CUSTOM: { type: CardType; label: string; blurb: string }[] = [
  { type: 'note', label: 'Note', blurb: 'Your own text (Markdown): instructions, on-call, goals.' },
  { type: 'links', label: 'Links', blurb: 'Shortcuts to Cosmos pages or any URL.' },
  { type: 'clock', label: 'Clock', blurb: 'Time and date, for wall displays.' },
];

export function CardEditor({ open, onOpenChange, card, metrics, onSave, onDelete, nextY }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** null = adding a new card. */
  card: Card | null;
  metrics: MetricDef[];
  onSave: (c: Card) => void;
  onDelete?: (id: string) => void;
  nextY: number;
}) {
  const [draft, setDraft] = useState<Draft | null>(card);
  const [q, setQ] = useState('');
  const [module, setModule] = useState<MetricModule | 'all' | 'custom'>('all');
  useEffect(() => { if (open) { setDraft(card); setQ(''); setModule('all'); } }, [open, card]);

  const def = draft?.metric ? metrics.find((m) => m.id === draft.metric) : undefined;
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return metrics.filter((m) => (module === 'all' || m.module === module) && (!t || `${m.label} ${m.description} ${MODULE_LABEL[m.module]}`.toLowerCase().includes(t)));
  }, [metrics, q, module]);
  const modules = useMemo(() => [...new Set(metrics.map((m) => m.module))], [metrics]);

  const pickMetric = (m: MetricDef) => {
    const type = draft && CARD_TYPES_FOR[m.kind].includes(draft.type) ? draft.type : CARD_TYPES_FOR[m.kind][0];
    const size = CARD_SIZE[type];
    setDraft((d) => ({ ...(d ?? { id: newCardId(), x: 0, y: nextY, ...size }), type, metric: m.id, title: d?.metric && d.metric !== m.id ? undefined : d?.title,
    }));
  };
  const pickCustom = (type: CardType) => setDraft((d) => ({ id: d?.id ?? newCardId(), x: d?.x ?? 0, y: d?.y ?? nextY, ...CARD_SIZE[type], type, options: type === 'links' ? { links: [] } : type === 'note' ? { text: '' } : {} }));
  const set = (patch: Partial<Card>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setOpt = (patch: Partial<NonNullable<Card['options']>>) => setDraft((d) => (d ? { ...d, options: { ...d.options, ...patch } } : d));

  const isCustom = draft && ['note', 'links', 'clock'].includes(draft.type);
  const showPicker = !draft || (!isCustom && !draft.metric) || module === 'custom' || (!card && !draft);
  const canSave = !!draft && (isCustom || !!draft.metric);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-4xl flex-col gap-0 p-0">
        <DialogHeader className="border-b border-border px-5 py-3">
          <DialogTitle>{card ? 'Edit card' : 'Add a card'}</DialogTitle>
        </DialogHeader>
        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px]">
          {/* Left: what the card shows */}
          <div className="flex min-h-0 flex-col border-b border-border md:border-b-0 md:border-r">
            <div className="flex flex-wrap gap-1 border-b border-divider px-4 py-2">
              {(['all', ...modules, 'custom'] as const).map((m) => (
                <button key={m} type="button" onClick={() => setModule(m)}
                  className={cn('rounded-full border px-2 py-0.5 text-[11.5px]', module === m ? 'border-ring bg-brand-soft text-brand' : 'border-border text-muted-foreground hover:text-foreground')}>
                  {m === 'all' ? 'All metrics' : m === 'custom' ? 'Custom cards' : MODULE_LABEL[m].split(' · ')[0]}
                </button>
              ))}
            </div>
            {module === 'custom' ? (
              <ul className="space-y-2 overflow-y-auto p-4">
                {CUSTOM.map((c) => (
                  <li key={c.type}>
                    <button type="button" onClick={() => pickCustom(c.type)}
                      className={cn('w-full rounded-md border px-3 py-2 text-left', draft?.type === c.type ? 'border-ring bg-brand-soft' : 'border-border hover:bg-muted/50')}>
                      <div className="text-sm font-medium text-foreground">{c.label}</div>
                      <div className="text-xs text-muted-foreground">{c.blurb}</div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <>
                <div className="relative px-4 py-2">
                  <Search className="pointer-events-none absolute left-6 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search metrics…" autoFocus={showPicker}
                    className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 text-sm outline-none focus:border-ring" />
                </div>
                <ul className="min-h-0 flex-1 divide-y divide-divider overflow-y-auto" style={{ maxHeight: '52vh' }}>
                  {filtered.map((m) => (
                    <li key={m.id}>
                      <button type="button" onClick={() => pickMetric(m)}
                        className={cn('w-full px-4 py-2 text-left', draft?.metric === m.id ? 'bg-brand-soft' : 'hover:bg-muted/50')}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{m.label}</span>
                          <span className="rounded bg-muted px-1 text-[10px] text-muted-foreground">{KIND_LABEL[m.kind]}</span>
                          {!m.scoped && <span className="rounded bg-muted px-1 text-[10px] text-muted-foreground" title="Estate-wide: can't be filtered to one client">all clients</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{MODULE_LABEL[m.module]} · {m.description}</div>
                      </button>
                    </li>
                  ))}
                  {filtered.length === 0 && <li className="px-4 py-6 text-center text-xs text-muted-foreground">No metric matches.</li>}
                </ul>
              </>
            )}
          </div>

          {/* Right: how it looks */}
          <div className="min-h-0 space-y-3 overflow-y-auto p-4 text-sm">
            {!draft ? <p className="text-xs text-muted-foreground">Pick a metric, or a custom card.</p> : (
              <>
                <Field label="Title">
                  <input value={draft.title ?? ''} onChange={(e) => set({ title: e.target.value || undefined })} placeholder={def?.label ?? CARD_LABEL[draft.type]}
                    className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-ring" />
                </Field>

                {def && CARD_TYPES_FOR[def.kind].length > 1 && (
                  <Field label="Show as">
                    <div className="flex gap-1">
                      {CARD_TYPES_FOR[def.kind].map((t) => (
                        <button key={t} type="button" onClick={() => set({ type: t })}
                          className={cn('rounded-md border px-2 py-1 text-xs', draft.type === t ? 'border-ring bg-brand-soft text-brand' : 'border-border text-muted-foreground')}>{CARD_LABEL[t]}</button>
                      ))}
                    </div>
                  </Field>
                )}

                {def?.scoped && (
                  <Field label="Client" hint="Follow the dashboard, or pin this card to one client.">
                    {draft.params?.companyId !== undefined ? (
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate rounded-md border border-border px-2 py-1 text-xs">{draft.params.companyId == null ? 'Always all clients' : `Pinned: ${draft.params.companyName ?? `#${draft.params.companyId}`}`}</span>
                        <button type="button" onClick={() => set({ params: { ...draft.params, companyId: undefined, companyName: undefined } })} className="text-xs text-muted-foreground hover:text-foreground">Follow dashboard</button>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <CompanySearch value={null} onChange={(c) => c && set({ params: { ...draft.params, companyId: c.id, companyName: c.name } })} placeholder="Pin to a client…" />
                        <button type="button" onClick={() => set({ params: { ...draft.params, companyId: null } })} className="text-xs text-muted-foreground hover:text-foreground">Always all clients</button>
                      </div>
                    )}
                  </Field>
                )}

                {def?.psaScope && (
                  <Field label="Whose" hint="“Mine” shows each viewer their own tickets / deals.">
                    <div className="flex gap-1">
                      {(['team', 'me'] as const).map((s) => (
                        <button key={s} type="button" onClick={() => set({ params: { ...draft.params, psaScope: s } })}
                          className={cn('rounded-md border px-2 py-1 text-xs', (draft.params?.psaScope ?? 'team') === s ? 'border-ring bg-brand-soft text-brand' : 'border-border text-muted-foreground')}>{s === 'team' ? 'Everyone' : 'Mine (viewer)'}</button>
                      ))}
                    </div>
                  </Field>
                )}

                {def?.kind === 'number' && def.better && (
                  <Field label="Colour when" hint={def.better === 'down' ? 'Amber / red at or above these values.' : 'Amber / red at or below these values.'}>
                    <div className="flex items-center gap-2 text-xs">
                      <label className="flex items-center gap-1 text-warning">Amber <NumInput value={draft.options?.thresholds?.warn} onChange={(v) => setOpt({ thresholds: { ...draft.options?.thresholds, warn: v } })} /></label>
                      <label className="flex items-center gap-1 text-destructive">Red <NumInput value={draft.options?.thresholds?.bad} onChange={(v) => setOpt({ thresholds: { ...draft.options?.thresholds, bad: v } })} /></label>
                    </div>
                  </Field>
                )}

                {def?.kind === 'number' && (
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input type="checkbox" checked={draft.options?.showTotal ?? true} onChange={(e) => setOpt({ showTotal: e.target.checked })} /> Show “of total” when the metric has one
                  </label>
                )}

                {(draft.type === 'list' || draft.type === 'bar' || draft.type === 'donut') && (
                  <Field label="Show at most"><NumInput value={draft.options?.limit ?? (draft.type === 'list' ? 10 : 12)} onChange={(v) => setOpt({ limit: v == null ? undefined : Math.max(1, Math.min(50, Math.round(v))) })} /></Field>
                )}

                {draft.type === 'note' && (
                  <Field label="Text" hint="Markdown: **bold**, lists, [links](https://…).">
                    <textarea value={draft.options?.text ?? ''} onChange={(e) => setOpt({ text: e.target.value })} rows={8} maxLength={5000}
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-ring" />
                  </Field>
                )}

                {draft.type === 'links' && <LinksEditor links={draft.options?.links ?? []} onChange={(links) => setOpt({ links })} />}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <div>{card && onDelete && <Button variant="ghost" size="sm" className="text-destructive" onClick={() => { onDelete(card.id); onOpenChange(false); }}><Trash2 className="h-3.5 w-3.5" /> Remove card</Button>}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" disabled={!canSave} onClick={() => { if (draft) { onSave(draft); onOpenChange(false); } }}>{card ? 'Apply' : 'Add card'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const KIND_LABEL = { number: 'number', breakdown: 'chart', series: 'trend', table: 'list' } as const;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function NumInput({ value, onChange }: { value: number | null | undefined; onChange: (v: number | null) => void }) {
  return (
    <input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      className="h-7 w-20 rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none focus:border-ring" />
  );
}

function LinksEditor({ links, onChange }: { links: { label: string; url: string }[]; onChange: (l: { label: string; url: string }[]) => void }) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const valid = label.trim() && (/^\//.test(url.trim()) || /^https?:\/\//i.test(url.trim()));
  return (
    <Field label="Links" hint="A Cosmos path (/rmm/alerts) or a full https:// URL.">
      <ul className="mb-2 space-y-1">
        {links.map((l, i) => (
          <li key={i} className="flex items-center gap-2 text-xs">
            <span className="min-w-0 flex-1 truncate"><b className="font-medium">{l.label}</b> <span className="text-muted-foreground">{l.url}</span></span>
            <button type="button" onClick={() => onChange(links.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
          </li>
        ))}
      </ul>
      <div className="flex gap-1">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" className="h-7 w-24 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-ring" />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/path or https://…" className="h-7 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-ring" />
        <Button type="button" size="sm" variant="outline" className="h-7 px-2" disabled={!valid || links.length >= 20}
          onClick={() => { onChange([...links, { label: label.trim(), url: url.trim() }]); setLabel(''); setUrl(''); }}><Plus className="h-3.5 w-3.5" /></Button>
      </div>
    </Field>
  );
}
